import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";

// TODO: eslint fix
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * One option for BpSelect
 */
export class BpSelectOption<M = any> {
	/**
	 * Name of the Option displayed in text mode and on mobile
	 */
	public name: string = "";
	/**
	 * Value of the Option
	 */
	// @ts-ignore
	public value: M = null;

	/**
	 * Optional icon form the bitpanda iconfont to be displayed in icon mode
	 */
	public icon?: string;

	/**
	 * Optional icon to be set as external received img path
	 */
	public iconUrl?: string;

	/**
	 * Optional text shown in the right part of each item (eg: wallets list, each one showing amount)
	 */
	public details?: string;
}

/**
 * Mobile Firendly Select with Icon support
 *
 * Example of usage:
 * <example-url>bp-select.demo.component.html</example-url>
 */
@Component({
	selector: "bp-select",
	templateUrl: "./bp-select.component.html",
	styleUrls: ["./bp-select.component.scss"],
	standalone: false
})
export class BpSelectComponent implements OnChanges {
	// --------- HOW IT WORKS
	// The component gets a "raw" value plugged into "selectedValue". Whenever changes of the value or the options
	// Are detected via ngOnChanges,the currently selected BpOption is set from the available options by referencing
	// The selectedValue.
	// ----------------------------------

	// @ts-ignore
	@ViewChild("ngSelect") private ngSelect: NgSelectComponent = null;

	@Input() public error: string = ""; // Takes any error-message

	@Input() public disabled: boolean = false;

	@Input() public appendTo: string = ""; // Attaches dropdown to e.g. a modal

	@Input() public bindLabel: string = "";

	@Input("isLoading") public isLoading: boolean = false;

	@Input() public dropdownPosition: string = ""; // Fixes position in which dropdown opens

	// @ts-ignore
	@Input() public customSearchFn: Function = null;

	@Input() public useEmptyState: boolean = false; // Enables empty-state functionality
	@Input() public placeholder: string = "";
	@Input() public bottomLabel: string = "";
	@Input() public label: string = "";
	@Input() public bottomLabelHyperlink: string = "";
	@Input() public showDetails: boolean = true;

	public addEmptyState(): boolean {
		return this.useEmptyState && !this.selected;
	}

	// --------- DROPDOWN OPTIONS
	private _options: BpSelectOption[] = [];

	@Input()
	public set options(options: BpSelectOption[]) {
		this._options = options;
	}

	public get options(): BpSelectOption[] {
		return this._options;
	}

	// ----------------------------------
	// ------------------ SELECTED OPTION + CHANGE EMIT
	// @ts-ignore
	private _selected: BpSelectOption = null;

	public set selected(option: BpSelectOption) {
		if (!option || (!!this._selected && this._selected.value === option.value)) {
			return;
		}
		if (!!this._selected || this.useEmptyState) {
			// In general you only want the change-emits to emit on an active change, NOT when the initial selection happens on startup
			this._selected = option; // Setting should happen before changeEmit
			this.selectedValueChange.emit(option.value);
		} else {
			this._selected = option;
		}
	}

	public get selected(): BpSelectOption {
		return this._selected;
	}

	@Output() public selectedValueChange: EventEmitter<any> = new EventEmitter<any>();

	// ----------------------------------
	// ------------------ PLUGGED IN VALUE FROM COMPONENT
	private _selectedValue: any = null;

	@Input()
	public set selectedValue(value: any) {
		this._selectedValue = value;
	}

	// ----------------------------------
	// ------------------ CHANGE DETECTION

	public ngOnChanges(changes: SimpleChanges): void {
		const changeItem = changes.valueOf();
		if (changeItem.hasOwnProperty("options") || changeItem.hasOwnProperty("selectedValue")) {
			if (!this.options) {
				return;
			}
			// @ts-ignore
			const selectedOption: BpSelectOption = this.options.find(
				(o) => !!o.value && o.value === this._selectedValue
			);
			this.selected = selectedOption;
			// In some cases, the name of the selected item changes even tho the value does not change
			// E.g. when switching to another language. In that case we want to update the selected item
			// Without triggering another emit.
			if (selectedOption?.name !== this._selected?.name) {
				this._selected = selectedOption;
			}
		}
	}

	// ----------------------------------
	// ------------------ CLEAR, SEARCH AND OTHER SPECIAL CASES

	// @ts-ignore
	private optionOnClear: BpSelectOption = null;

	public onClear(): void {
		this.optionOnClear = this._selected;
		// @ts-ignore
		this._selected = null; // On clear clears the dropdown selection but NOT the model -> has to be cleared manually
		this.searchQuery = "";
	}

	@Input() public searchTermOnClose: string = "";

	@Input() public clearable: boolean = false;

	@Output("searchOutput")
	public searchQueryEmitter: EventEmitter<string> = new EventEmitter<string>();
	private _searchQuery: string = "";

	public get searchQuery(): string {
		return this._searchQuery;
	}

	public set searchQuery(query: string) {
		if (query !== this._searchQuery && this.searchable) {
			this._searchQuery = query;
			this.searchQueryEmitter.emit(this._searchQuery);
		}
	}

	public enableClearFunction(e: any): void {
		this.searchQuery = e.term;
		if (this.searchQuery.length > 0) {
			this.clearable = true;
		} // A field should only be clearable while the search is active
	}

	@Input()
	public searchable: boolean = false;
	private saveSearchableState: boolean = false; // Used to temp-save search-status of the dropdown

	public onOpen(): void {
		if (this.searchable) {
			// Necessary to enable search function again on open
			this.saveSearchableState = this.searchable;
		}
		this.searchable = this.saveSearchableState;
	}

	public closeDropdown(searchTerm: string = ""): void {
		if (searchTerm?.length > 0 || (this.useEmptyState && !searchTerm)) {
			this.searchQuery = "";
			this.clearable = false;
			this.searchable = false; // Necessary to set this to false on dropdown select to get rid of the possibility for input
			if (!this.selected) {
				const find = this.options.find((w) => w.name === searchTerm);
				if (find) {
					this.selected = find;
				}
			}
		}

		if (this.optionOnClear) {
			// This is only true if the clear-button was used inside the input field
			if (!this.selected) {
				// Only triggered if the clear-button was used AND the dropdown was closed without selecting an option after clearing
				this.selected = this.optionOnClear;
			}
			// @ts-ignore
			this.optionOnClear = null;
			this.selectedValueChange.emit(this._selected.value); // In case optionOnClear was set, it means selected has been cleared once. that makes a manual emit necessary
		}

		// Hotfix for android chrome bug
		// https://github.com/ng-select/ng-select/issues/1451
		if (this.ngSelect) {
			this.ngSelect.blur();
			this.ngSelect.focus();
		}
	}

	// Custom cases

	@Output("scrollToEnd")
	public scrollToEndEmitter: EventEmitter<void> = new EventEmitter<void>();

	public onScrollToEnd(): void {
		this.scrollToEndEmitter.emit();
	}

	@Input() public countrySelection: boolean = false;

	public isCountrySelection(): boolean {
		return this.countrySelection;
	}
}
